// Spectator and death camera: a third-person follow of the watched player (the killer after a
// death, a teammate in Defusal). The camera hangs on a boom that rises above the head and reaches
// back over the right shoulder; walls pull it in, it never enters the head and the watched model is
// hidden when the camera has to come so close that the body would fill the view.

import type { Vec3Like } from "../wasm/types";
import { clamp, wrapAngle } from "./angles";
import type { CameraPose } from "./camera";
import { approachExp } from "./cameraMath";

/** Distance to the first wall along a normalized ray, or undefined within `maxDist`. */
export type RayDist = (
  ox: number,
  oy: number,
  oz: number,
  dx: number,
  dy: number,
  dz: number,
  maxDist: number,
) => number | undefined;

export interface FollowTarget {
  id: number;
  /** Feet position. */
  pos: Vec3Like;
  /** Radians, as in the snapshots. */
  yaw: number;
  pitch: number;
  crouched: boolean;
  sliding: boolean;
}

export const FOLLOW = {
  /** Rise of the boom's pivot above the head centre (m). */
  lift: 0.5,
  /** Reach of the boom behind the pivot and to the right of it (m). */
  back: 2.4,
  side: 0.45,
  /** The camera stops this far before a wall along the boom (the near plane's corners are 0.09 m out). */
  wallPad: 0.2,
  /** ...and stays at least this far from any wall around it (probed in 26 directions). */
  nearClear: 0.15,
  /** The camera never comes closer to the head centre than this: the head sphere (0.16) plus the near plane. */
  headKeepOut: 0.27,
  /** Closer to the head centre than this, the watched model is hidden (it would fill the view). */
  hideWithin: 0.45,
  /** Time constant of the view angles following the watched player's (s): a flick reads as a swing. */
  angleTau: 0.1,
  /** Time constant of the boom growing back after a wall let go of it (s); it shrinks at once. */
  boomOutTau: 0.3,
  /** Share of the watched pitch that tilts the boom (looking down raises the camera). */
  orbitPitch: 0.5,
  /** The camera looks this much lower than the watched player (rad), so the body stays in view. */
  lookDown: 0.1,
} as const;

/**
 * Centre of the visible head (feet-relative height and distance ahead along the yaw) per stance,
 * measured on the character rig (within a few centimetres of the head sphere of `gr-core/src/hitbox.rs`).
 */
const HEAD = {
  stand: { y: 1.65, ahead: 0 },
  crouch: { y: 1.08, ahead: 0.19 },
  slide: { y: 0.7, ahead: -0.66 },
} as const;

/** Writes the head centre of `t` to `out`. Yaw 0 looks down -Z, like the rig. */
export function headCentre(
  t: FollowTarget,
  out: { x: number; y: number; z: number },
): void {
  const h = t.sliding ? HEAD.slide : t.crouched ? HEAD.crouch : HEAD.stand;
  out.x = t.pos.x - Math.sin(t.yaw) * h.ahead;
  out.y = t.pos.y + h.y;
  out.z = t.pos.z - Math.cos(t.yaw) * h.ahead;
}

/** Directions tried when the boom would end inside the head: behind, up, the sides, in front. */
const ESCAPES = [
  [0, 0, 1],
  [0, 1, 0],
  [1, 0, 0],
  [-1, 0, 0],
  [0, 0, -1],
] as const;

/** Unit directions to the faces, edges and corners of a cube: probed around the camera for walls closer than `nearClear`. */
const PROBES: readonly (readonly [number, number, number])[] = (() => {
  const out: [number, number, number][] = [];
  for (let x = -1; x <= 1; x++) {
    for (let y = -1; y <= 1; y++) {
      for (let z = -1; z <= 1; z++) {
        if (x === 0 && y === 0 && z === 0) continue;
        const n = Math.hypot(x, y, z);
        out.push([x / n, y / n, z / n]);
      }
    }
  }
  return out;
})();

export class FollowCam {
  readonly pose: CameraPose = {
    pos: { x: 0, y: 0, z: 0 },
    yaw: 0,
    pitch: 0,
    roll: 0,
    hFovDeg: 103,
  };
  /** The watched model should not be drawn this frame (the camera is too close to it). */
  hideTarget = false;

  private targetId = -1;
  private yaw = 0;
  private pitch = 0;
  private boom = 0;
  private readonly head = { x: 0, y: 0, z: 0 };

  /** Forget the watched player: the next `update` snaps to whoever it is given. */
  reset(): void {
    this.targetId = -1;
    this.hideTarget = false;
  }

  /** Has followed somebody since the last reset (its pose is where the view was left). */
  get active(): boolean {
    return this.targetId >= 0;
  }

  update(
    dt: number,
    t: FollowTarget,
    ray: RayDist,
    baseFovDeg: number,
  ): CameraPose {
    const fresh = t.id !== this.targetId;
    this.targetId = t.id;
    if (fresh) {
      this.yaw = t.yaw;
      this.pitch = t.pitch;
    } else {
      this.yaw = wrapAngle(
        this.yaw +
          wrapAngle(t.yaw - this.yaw) * (1 - Math.exp(-dt / FOLLOW.angleTau)),
      );
      this.pitch = approachExp(this.pitch, t.pitch, FOLLOW.angleTau, dt);
    }

    const head = this.head;
    headCentre(t, head);

    // The pivot rises above the head as far as the ceiling lets it.
    const up = ray(
      head.x,
      head.y,
      head.z,
      0,
      1,
      0,
      FOLLOW.lift + FOLLOW.wallPad,
    );
    const lift =
      up === undefined ? FOLLOW.lift : Math.max(0, up - FOLLOW.wallPad);
    const px = head.x;
    const py = head.y + lift;
    const pz = head.z;

    // The boom: behind along the smoothed view, tilted by part of the pitch, over the right shoulder.
    const sy = Math.sin(this.yaw);
    const cy = Math.cos(this.yaw);
    const op = clamp(this.pitch * FOLLOW.orbitPitch, -0.6, 0.6);
    const cp = Math.cos(op);
    const sp = Math.sin(op);
    // forward = (-sin(yaw) cos(p), sin(p), -cos(yaw) cos(p)), right = (cos(yaw), 0, -sin(yaw)).
    let bx = sy * cp * FOLLOW.back + cy * FOLLOW.side;
    let by = -sp * FOLLOW.back;
    let bz = cy * cp * FOLLOW.back - sy * FOLLOW.side;
    const len = Math.hypot(bx, by, bz);
    bx /= len;
    by /= len;
    bz /= len;
    const hit = ray(px, py, pz, bx, by, bz, len + FOLLOW.wallPad);
    const allowed = hit === undefined ? len : Math.max(0, hit - FOLLOW.wallPad);
    this.boom =
      fresh || allowed < this.boom
        ? allowed
        : approachExp(this.boom, allowed, FOLLOW.boomOutTau, dt);

    const c = this.pose.pos;
    c.x = px + bx * this.boom;
    c.y = py + by * this.boom;
    c.z = pz + bz * this.boom;
    keepOffWalls(c, ray);
    keepOutOfHead(c, head, this.yaw, ray);

    this.hideTarget =
      Math.hypot(c.x - head.x, c.y - head.y, c.z - head.z) < FOLLOW.hideWithin;
    const p = this.pose;
    p.yaw = this.yaw;
    p.pitch = clamp(this.pitch - FOLLOW.lookDown, -1.45, 1.45);
    p.roll = 0;
    p.hFovDeg = baseFovDeg;
    return p;
  }
}

/** Pushes `c` away from walls closer than `nearClear` around it. */
function keepOffWalls(
  c: { x: number; y: number; z: number },
  ray: RayDist,
): void {
  for (const [dx, dy, dz] of PROBES) {
    const h = ray(c.x, c.y, c.z, dx, dy, dz, FOLLOW.nearClear);
    if (h === undefined) continue;
    const push = FOLLOW.nearClear - h;
    c.x -= dx * push;
    c.y -= dy * push;
    c.z -= dz * push;
  }
}

/**
 * A camera that ended inside the head keep-out (walls behind and above the head) moves to the
 * first free side of the head instead, in the yaw frame of the view.
 */
function keepOutOfHead(
  c: { x: number; y: number; z: number },
  head: Vec3Like,
  yaw: number,
  ray: RayDist,
): void {
  if (
    Math.hypot(c.x - head.x, c.y - head.y, c.z - head.z) >= FOLLOW.headKeepOut
  )
    return;
  const sy = Math.sin(yaw);
  const cy = Math.cos(yaw);
  const reach = FOLLOW.headKeepOut + FOLLOW.nearClear;
  for (const [back, up, right] of ESCAPES) {
    // Behind is +back along -forward; right is the view's right.
    const dx = back * sy + right * cy;
    const dy = up;
    const dz = back * cy - right * sy;
    if (ray(head.x, head.y, head.z, dx, dy, dz, reach) !== undefined) continue;
    c.x = head.x + dx * FOLLOW.headKeepOut;
    c.y = head.y + dy * FOLLOW.headKeepOut;
    c.z = head.z + dz * FOLLOW.headKeepOut;
    return;
  }
}
